import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {map, shareReplay, tap} from 'rxjs/operators';
import {CustomerService} from '../_services/configuration-services';

@Injectable({providedIn: 'root'})
export class CustomerContextService {

    private currentCustomerId: string | null = null;
    readonly currentCustomerId$ = new BehaviorSubject<string | null>(null);

    private pendingLoad$?: Observable<string | null>;

    constructor(private readonly customerService: CustomerService) {}

    /**
     * Returns the database id of the currently logged-in customer (Customer.Id), populated by the
     * bootstrap getMe() call in AppComponent or lazily via loadCurrentCustomerId().
     * NOT the same as RolesService.getAccountId(), which returns the ADB2CObjectId from the token and
     * differs from Customer.Id for dissociated accounts. Use this id to compare against persisted
     * *.customerId (e.g. BookingDto.customerId) — never the token claim.
     */
    getCurrentCustomerId(): string | null {
        return this.currentCustomerId;
    }

    setCurrentCustomerId(id: string | null): void {
        this.currentCustomerId = id;
        this.currentCustomerId$.next(id);
    }

    /**
     * Resolves the logged-in customer's Customer.Id, fetching it once via getMe() and caching it, so it
     * can be used regardless of the AppComponent bootstrap timing. Must only be called for authenticated
     * customer accounts (the getMe endpoint requires the Customer role).
     */
    loadCurrentCustomerId(): Observable<string | null> {
        if (this.currentCustomerId !== null) {
            return of(this.currentCustomerId);
        }

        if (!this.pendingLoad$) {
            this.pendingLoad$ = this.customerService.getMe().pipe(
                map(customer => customer.id ?? null),
                tap(id => this.setCurrentCustomerId(id)),
                shareReplay(1)
            );
        }

        return this.pendingLoad$;
    }
}
